import Constants from "expo-constants";
import { Address } from "./address";

const ADDRESS_VALIDATION_URL = "https://addressvalidation.googleapis.com/v1:validateAddress";

type AddressValidationResult = {
    result?: {
        address?: {
            missingComponentTypes?: string[];
        };
    };
};

/**
 * Determines, via the Google Address Validation API, whether the given address is missing a
 * street number that its street is actually expected to have.
 *
 * Google lists "street_number" in `missingComponentTypes` only when a number is expected for the
 * street but was not provided; streets that genuinely have no numbers never list it. This lets us
 * require the most precise address possible without trapping users whose street has no number.
 *
 * Fails open (returns false) on missing key or any network/API error so a validation outage never
 * blocks the user. Requires the Address Validation API to be enabled on the Google Cloud project.
 */
export const isStreetNumberMissing = async (address: Address): Promise<boolean> => {
    const apiKey = Constants.expoConfig?.extra?.googleApiKey;
    if (!apiKey) {
        return false;
    }
    try {
        const response = await fetch(`${ADDRESS_VALIDATION_URL}?key=${apiKey}`, {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
                address: {
                    regionCode: address.locality.state.country.code,
                    addressLines: [address.formatted],
                },
            }),
        });
        if (!response.ok) {
            console.warn("Address validation request failed", response.status);
            return false;
        }
        const data = (await response.json()) as AddressValidationResult;
        return data.result?.address?.missingComponentTypes?.includes("street_number") ?? false;
    } catch (error) {
        console.warn("Address validation request errored", error);
        return false;
    }
};
